/**
 * English catalogue.
 *
 * This is the reference: `bg.ts` mirrors its shape exactly, and a test fails
 * if the two ever drift. Keys are grouped by surface, not by page, so shared
 * strings stay shared instead of being re-translated per screen.
 */
import monitoring from './en/monitoring'
import trademarks from './en/trademarks'
import general from './en/general'
import people from './en/people'
import explain from './en/explain'

export default {
  nav: {
    workspace: 'Workspace',
    workspaceTools: 'Workspace tools',
    dashboard: 'Dashboard',
    monitoring: 'TM Monitoring',
    trademarks: 'My Trademarks',
    oppositions: 'Oppositions',
    reports: 'Reports',
    clients: 'Clients',
    settings: 'Settings',
  },

  topbar: {
    openMenu: 'Open menu',
    closeMenu: 'Close menu',
    search: 'Search trademarks, owners, classes…',
    searchLabel: 'Search',
    openSearch: 'Open global search',
    notifications: 'Notifications',
    notificationsUnread: 'Notifications, {n} unread',
    help: 'Help',
    account: 'Account',
  },

  account: {
    profile: 'Profile',
    settings: 'Settings',
    security: 'Security',
    help: 'Help center',
    signOut: 'Sign out',
    language: 'Language',
    menuLabel: 'Account',
    /* The account page (W11): what `PATCH /users/:id` lets a person change
     * about themselves. */
    page: {
      title: 'Your account',
      subtitle: 'Your name, your language and your password.',
      loadFailed: 'Your account could not be loaded.',
      profile: 'Profile',
      profileSub: 'How you appear to colleagues and on case timelines.',
      name: 'Full name',
      email: 'Sign-in email',
      emailHint: 'Your sign-in address is changed by the firm, not here.',
      tier: 'Account type',
      firm: 'Firm',
      tiers: {
        firm_main: "The firm's main account",
        firm_manager: 'Manager',
        client_viewer: 'Client account (read only)',
      },
      save: 'Save profile',
      saved: 'Profile saved',
      failed: 'The profile was not saved',
      nameRequired: 'Enter your name.',
      password: 'Password',
      passwordSub: 'Changing it asks for your current password first.',
      current: 'Current password',
      new: 'New password',
      repeat: 'Repeat the new password',
      minLength: 'At least {n} characters.',
      tooShort: 'The new password needs at least {n} characters.',
      needCurrent: 'Enter your current password.',
      mismatch: 'The two new passwords are not the same.',
      change: 'Change password',
      changed: 'Password changed',
      wrongCurrent: 'Your current password is not correct.',
      changeFailed: 'The password was not changed',
      notYet:
        'Two-factor sign-in, a list of signed-in devices and API tokens are not in the app yet.',
    },
  },

  auth: {
    title: 'Welcome back',
    subtitle: 'Sign in to manage your trademark portfolio.',
    email: 'Work email',
    // `{'@'}` is vue-i18n's literal escape: a bare @ is parsed as a link.
    emailPlaceholder: "you{'@'}firm.com",
    password: 'Password',
    remember: 'Remember me',
    forgot: 'Forgot password?',
    signIn: 'Sign in',
    signingIn: 'Signing in…',
    noAccount: "Don't have an account?",
    requestAccess: 'Request access',
    welcomeBack: 'Welcome back',
    failed: 'Sign-in failed',
    checkCredentials: 'Check your credentials and try again.',
    enterEmail: 'Enter your email',
    enterPassword: 'Enter your password',
    /* Forgot password and the e-mail link page (W13.M3). */
    reset: {
      title: 'Reset your password',
      sub: 'Enter the email you sign in with. If it has an account, we send it a link to set a new password.',
      submit: 'Send the link',
      sending: 'Sending…',
      sent: 'If that address has an account, a link is on its way.',
      sentSub:
        'The link works once, for 30 minutes. If nothing arrives, check the spam folder or ask again.',
      back: 'Back to sign in',
      failed: 'The request could not be sent. Try again in a moment.',
    },
    link: {
      checking: 'Checking the link…',
      invalid: 'This link is no longer valid',
      invalidSub: 'A link works once and expires. Ask for a new one below.',
      askNew: 'Ask for a new link',
      unreachable: 'The link could not be checked',
      resetTitle: 'Set a new password for {email}',
      inviteTitle: 'Choose your password for {email}',
      resetSub: 'The new password replaces your old one, and you are signed in straight away.',
      inviteSub:
        'You have been invited to Lexentiqa. Choose a password and you are signed in straight away.',
      expires: 'This link expires {when}.',
      password: 'New password',
      confirm: 'Repeat the password',
      minLength: 'At least {n} characters.',
      tooShort: 'The password needs at least {n} characters.',
      mismatch: 'The two passwords are not the same.',
      submit: 'Set password and sign in',
      submitting: 'Saving…',
      done: 'Password set — you are signed in',
      failed: 'The password was not set',
    },
  },

  common: {
    save: 'Save',
    cancel: 'Cancel',
    confirm: 'Confirm',
    delete: 'Delete',
    edit: 'Edit',
    close: 'Close',
    retry: 'Try again',
    loading: 'Loading…',
    search: 'Search',
    clearAll: 'Clear all',
    skipToContent: 'Skip to main content',
  },

  states: {
    emptyTitle: 'Nothing here yet',
    emptyMessage: 'Nothing matches the current filters.',
    errorTitle: 'Something went wrong',
    errorMessage: 'The page could not be loaded.',
    notFoundTitle: 'Page not found',
    offline: 'You appear to be offline.',
  },

  time: {
    dueToday: 'Due today',
    daysLeft: '{n} day left | {n} days left',
    daysOverdue: '{n} day overdue | {n} days overdue',
  },

  /* The bell and the notifications page (W11). The words of each
   * notification come from the server, already in the reader's language. */
  notif: {
    subtitle:
      'What happened on your cases and in the monitoring, newest first. Each one opens what it is about.',
    popupNew: '{n} new',
    popupEmpty: 'Nothing here yet.',
    viewAll: 'View all notifications',
    markAllRead: 'Mark all as read',
    markAllTitle: 'Mark every notification as read?',
    markAllBody: 'This covers all of them, not only the ones on this page.',
    markAllDone: 'All marked as read',
    settingsLink: 'Notification settings',
    failed: 'That change was not saved',
    loadFailed: 'The notifications could not be loaded.',
    count: '{n} notification | {n} notifications',
    unreadCount: '{n} unread',
    unread: 'unread',
    markRead: 'Mark as read',
    remove: 'Delete notification',
    range: '{from}–{to} of {total}',
    prev: 'Previous page',
    next: 'Next page',
    tabs: {
      all: 'All',
      unread: 'Unread',
      match_found: 'Matches',
      deadline_approaching: 'Deadlines',
      procedure_update: 'Case updates',
      system: 'Announcements',
    },
    empty: {
      allTitle: 'No notifications yet',
      allMessage:
        'When the monitoring finds a high-risk match, a deadline on a case comes close, or a case you work on changes, it appears here.',
      unreadTitle: 'All caught up',
      unreadMessage: 'Nothing is waiting to be read.',
      kindTitle: 'None of this kind',
      kindMessage: 'Nothing of this kind has been sent to you.',
    },
  },

  /* Notification preferences — the kind × channel matrix on Settings. */
  prefs: {
    title: 'Notification preferences',
    sub: 'For each kind of event, whether it reaches you in the app, by email, or both.',
    event: 'Event',
    channel: {
      in_app: 'In the app',
      email: 'By email',
    },
    kind: {
      match_found: {
        title: 'High-risk matches',
        sub: 'New high-risk matches on trademarks you can see — one summary per scan, not one per match.',
      },
      deadline_approaching: {
        title: 'Case deadlines',
        sub: 'A week before, the day before, on the day, and once it has passed — for cases assigned to you.',
      },
      procedure_update: {
        title: 'Case updates',
        sub: 'Status changes, notes, documents and deadlines on cases assigned to you — never your own.',
        viewer: 'Updates the firm shares with you on your cases.',
      },
      system: {
        title: 'Announcements',
        sub: 'Notices about the platform itself, such as planned maintenance.',
      },
    },
    defaultNote: 'A switch you have never changed is on.',
    emailNote:
      'Whether emails are sent at all is decided for the whole platform by its operator; these switches decide only whether you receive them.',
    failed: 'Your notification setting was not saved',
    loadFailed: 'Your notification settings could not be loaded.',
  },

  /* Scan reports (W11): one report per day the monitoring ran. */
  reports: {
    title: 'Scan reports',
    subtitle:
      'One report for each day the monitoring ran on your trademarks: what it compared and what it found.',
    dayCount: '{n} day with a report | {n} days with a report',
    newestFirst: 'Newest first',
    loadFailed: 'The reports could not be loaded.',
    emptyTitle: 'No scans yet',
    emptyMessage:
      'When the monitoring runs on your trademarks, each day it runs gets a report here.',
    col: {
      day: 'Day',
      brands: 'Trademarks checked',
      compared: 'Filings compared',
      matches: 'Matches found',
      high: 'High risk',
      judged: 'Read by the AI reviewer',
    },
    noScan: 'No scan run recorded',
    open: 'Open the report for {day}',
    pdfFor: 'Download the report for {day} as a PDF',
    htmlFor: 'Download the report for {day} as a printable page',
    range: '{from}–{to} of {total}',
    prev: 'Previous page',
    next: 'Next page',
    note: 'Filings compared counts the pairs the matcher scored against your trademarks; matches are the pairs it kept. A day with matches but no scan run recorded is from before scans were logged.',
    pdf: 'Download PDF',
    html: 'Printable page',
    pdfUnavailable: 'This server cannot make PDFs at the moment',
    pdfUnavailableSub:
      'The printable page has the same content: download it and print it from your browser.',
    pdfUnavailableCase: 'The platform operator can switch PDF output on for the server.',
    downloadFailed: 'The download failed',
    state: {
      new: 'New',
      reviewed: 'Reviewed',
      dismissed: 'Dismissed',
      escalated: 'Case opened',
    },
    detail: {
      title: 'Scan report · {day}',
      noRun: 'No scan run was recorded on this day; the matches below were.',
      ran: 'Ran {from}–{to} UTC',
      runs: '{n} scan run | {n} scan runs',
      totals: 'The day in numbers',
      comparedHint: 'Candidate pairs scored',
      judgedHint: 'The rest were decided by matching rules',
      matchesTitle: 'Matches found',
      matchesSub: 'Every match recorded on this day that you can see.',
      noMatches: 'No match was recorded on this day.',
      brandsTitle: 'Trademarks checked',
      brandsSub: "Each trademark the day's scans compared, most high-risk matches first.",
      noBrands: 'No trademark was scanned on this day.',
      col: {
        trademark: 'Trademark',
        found: 'Found',
        registry: 'Registry',
        risk: 'Risk',
        decidedBy: 'Decided by',
        state: 'Review',
        deadline: 'Opposition deadline',
        client: 'Client',
        registries: 'Registries',
      },
      judge: 'AI reviewer',
      rules: 'Matching rule',
      showAll: 'Show all {n}',
      showFewer: 'Show fewer',
      truncated: 'Listing the first {shown} of {total} matches.',
      back: 'Back to reports',
      loadFailed: 'This report could not be loaded.',
    },
  },

  /* Settings (W11). */
  settings: {
    title: 'Settings',
    subtitle: "Your language and notifications, and the firm's registry connections.",
    subtitleViewer: 'Your language and your notifications.',
    sections: 'Settings sections',
    mainOnly: "Only the firm's main account can change this.",
    nav: {
      language: 'Language',
      notifications: 'Notifications',
      firm: 'Firm',
      registries: 'National registries',
      people: 'People',
      later: 'Not in the app yet',
    },
    language: {
      title: 'Language',
      sub: 'The language of this app, and of the emails and notifications sent to you.',
      emails: 'Emails and notifications are written to you in {language}.',
      mismatch: 'Your emails are still written in {stored}.',
      useCurrent: 'Write them in {language} too',
      failed:
        'The language changed here but was not saved to your account, so emails keep their language.',
    },
    firm: {
      title: 'Firm',
      sub: 'Your firm as it appears on printed reports.',
      name: 'Firm name',
      email: 'Contact email',
      save: 'Save firm details',
      saved: 'Firm details saved',
      failed: 'The firm details were not saved',
      nameRequired: 'Enter the firm name.',
      loadFailed: 'The firm details could not be loaded.',
    },
    registries: {
      title: 'National registries',
      sub: "How the platform reaches a country's own trademark office, for the national monitoring scope.",
      credentials:
        'Passwords and keys are never entered here. The platform operator sets them up on the server.',
      add: 'Add a registry',
      country: 'Country code',
      countryHint: 'Two letters, such as BG.',
      type: 'Connection',
      kind: {
        api: 'Web API',
        ftp: 'FTP server',
      },
      address: 'Address',
      addressHint: "The registry feed's server or web address — without any user name or password.",
      useIt: 'Use this connection',
      save: 'Save connection',
      badCountry: 'Enter a two-letter country code.',
      exists: 'That country is already connected; edit it instead.',
      saved: '{country} connection saved',
      failed: 'The connection was not saved',
      remove: 'Remove',
      removeTitle: 'Remove the {country} connection?',
      removeBody:
        'The platform stops using it. Credentials held by the platform operator are not touched.',
      removed: 'Connection removed',
      loadFailed: 'The registry connections could not be loaded.',
      empty: 'No national registry is connected',
      emptyMessage:
        "The national scope watches a country's own trademark office. Connecting one records how to reach it.",
      active: 'In use',
      paused: 'Not in use',
      credOnFile: 'Credentials: set up by the platform operator.',
      credMissing: 'Credentials: not set up yet — ask the platform operator.',
      adapterYes: 'The platform can read this registry.',
      adapterNo:
        'The platform cannot read this registry yet. The connection is recorded; no national scan runs until it can.',
      other: 'Also stored: {keys}',
      updated: 'Updated {date}',
    },
    people: {
      title: 'People',
      sub: 'Colleagues and people at clients who can sign in, and what each of them can see.',
      open: 'Manage people',
      mainOnly: 'Only the firm’s main account adds people and chooses what they can see.',
    },
    later: {
      title: 'Not in the app yet',
      sub: 'Asked about often, and not something this page can do yet.',
      signinTitle: 'Sign-in security',
      signin: 'Two-factor sign-in and single sign-on are not available yet.',
      matchingTitle: 'How conflicts are scored',
      matching:
        'The matching engine is tuned for the whole platform by its operator, not per firm.',
    },
  },

  /* Opposition cases (W10). The API says "procedure"; nothing a lawyer reads
   * does. Registry codes (EUIPO, WIPO) are printed from the data, not from
   * here, because they are names and do not translate. */
  opp: {
    title: 'Opposition cases',
    caseRef: 'Case {ref}',
    registryBg: 'BG Patent Office',
    status: {
      under_review: 'Under review',
      drafting: 'Drafting',
      filed: 'Opposition filed',
      awaiting_response: 'Awaiting response',
      negotiation: 'Negotiation',
      resolved: 'Resolved',
      closed: 'Closed',
    },
    outcome: {
      won: 'Won',
      settled: 'Settled',
      lost: 'Lost',
      withdrawn: 'Withdrawn',
    },
    priority: {
      urgent: 'Urgent',
      high: 'High',
      medium: 'Medium',
      low: 'Low',
    },
    risk: {
      high: 'High risk',
      med: 'Medium risk',
      low: 'Low risk',
      none: 'No match behind this case, so no risk score',
    },
    verdict: {
      conflict: 'Conflict',
      strong_match: 'Conflict',
      unclear: 'Needs a human decision',
      review: 'Needs a human decision',
      no_conflict: 'No conflict',
      dismiss: 'No conflict',
    },
    kind: {
      statutory: 'Statutory',
      internal: 'Internal',
    },
    noteCategory: {
      legal: 'Legal',
      internal: 'Internal',
      status: 'Status update',
    },
    docCategory: {
      filing: 'Filing',
      evidence: 'Evidence',
      correspondence: 'Correspondence',
      decision: 'Decision',
      agreement: 'Agreement',
      other: 'Other',
    },
    deadline: {
      provisional: 'Provisional: rule under review',
      provisionalShort: 'provisional',
      provisionalHelp:
        "This date follows a registry rule the firm's lawyers have not confirmed yet. Check it before relying on it.",
      none: 'No open deadline',
      finished: 'Case finished',
      done: 'Done {date}',
      fromMark: "Taken from the registry's publication",
      fromRule: 'Set by a deadline rule',
      fromFirm: 'Added by the firm',
    },
    list: {
      subtitle:
        'Every conflict the firm has opened a dossier on: its status, its next deadline and who is working on it.',
      filters: 'Filters',
      create: 'Create opposition case',
      cases: '{n} case | {n} cases',
      filtersOn: '{n} filter on | {n} filters on',
      search: 'Search title, case number, trademark or client…',
      searchLabel: 'Search cases',
      range: 'Showing {from}–{to} of {total}',
      prev: 'Previous page',
      next: 'Next page',
      emptyTitle: 'No opposition cases yet',
      emptyMessage:
        'A case is opened from a match on the monitoring page, or by hand from this page.',
      toMonitoring: 'Go to monitoring',
      noMatchTitle: 'No cases match these filters',
      noMatchMessage: 'Clear the filters to see every case.',
      clearFilters: 'Clear the filters',
      loadFailed: 'The cases could not be loaded.',
    },
    kpi: {
      active: 'Active cases',
      openedThisMonth: '{n} opened this month',
      urgent: 'Overdue or due within 14 days',
      overdue: '{n} overdue',
      due14: '{n} due within 14 days',
      awaiting: 'Awaiting response',
      awaitingHint: 'Filed and waiting on the other side',
      won: 'Won or settled this year',
      wonHint: 'of {n} cases resolved this year',
    },
    filter: {
      registry: 'Registry',
      status: 'Status',
      deadline: 'Next deadline',
      deadlineNote: 'Counted over active cases.',
      overdue: 'Overdue',
      within: 'Within {n} days',
      any: 'Any date',
      priority: 'Priority',
      activeOnly: 'Active cases only',
      client: 'Client: {name}',
      brand: 'Trademark: {name}',
      search: 'Search: {q}',
    },
    sort: {
      label: 'Sort order',
      deadline: 'Next deadline first',
      recent: 'Recently updated',
      priority: 'Highest priority first',
      created: 'Newest cases first',
    },
    col: {
      conflict: 'Trademark conflict',
      risk: 'Match risk',
      status: 'Status',
      deadline: 'Next deadline',
      lawyer: 'Lawyer',
    },
    preview: {
      label: 'Case preview',
      show: 'Show in the preview',
      close: 'Close preview',
      origin: 'Where this case came from',
      judged: 'Read by the AI judge',
      rules: 'Surfaced by a matching rule',
      byHand: 'Opened by hand. No match stands behind it, so it has no risk score.',
      viewMatch: 'See how the match was found',
      nextDeadline: 'Next deadline',
      open: 'Open case dossier',
    },
    fact: {
      lawyer: 'Assigned lawyer',
      unassigned: 'Unassigned',
      client: 'Client',
      documents: 'Documents',
      files: '{n} file | {n} files',
      notes: 'Notes',
      noteCount: '{n} note | {n} notes',
      lastActivity: 'Last activity',
      grounds: 'Grounds',
      notRecorded: 'Not recorded yet',
      caseNumber: 'Case number',
      priority: 'Priority',
      registry: 'Registry',
      filed: 'Filing date',
      createdBy: 'Opened by',
      internalNotes: 'Working notes',
    },
    activity: {
      title: 'Recent legal activity',
      sub: 'The newest changes across every case you can see.',
      empty: 'Nothing has happened on any case yet.',
    },
    event: {
      created: 'Case opened by hand',
      createdFromMatch: 'Case opened from a monitoring match',
      status: 'Status changed from {from} to {to}',
      statusOutcome: 'Status changed from {from} to {to}: {outcome}',
      note: 'Note added ({category})',
      documentAdded: 'Document added: {name}',
      documentRemoved: 'Document removed: {name}',
      documentUpdated: 'Document details changed: {name}',
      assigned: 'Assigned to {name}',
      assignedSomeone: 'Lawyer assignment changed',
      unassigned: 'Lawyer unassigned',
      deadlineAdded: 'Deadline added: {title}, due {date}',
      deadlineDone: 'Deadline done: {title}',
      deadlineReopened: 'Deadline reopened: {title}',
      deadlineEdited: 'Deadline changed: {title}',
      deadlineRemoved: 'Deadline removed: {title}',
      priority: 'Priority changed from {from} to {to}',
      edited: 'Case details edited',
      windowClosed:
        'The opposition window on this mark closed on {date}, before the case was opened',
      other: 'Case updated',
      someone: 'Someone',
      clientVisible: 'Visible to client',
    },
    create: {
      crumb: 'New case',
      title: 'Open an opposition case',
      subtitle:
        "A case is the dossier for one conflict: your client's trademark against one filing.",
      submit: 'Open case',
      submitting: 'Opening…',
      draftFailed:
        'That match could not be loaded. It may have been removed, or you may not have access to it.',
      existingTitle: 'This match already has an open case',
      existingBody: 'A match can have one open case at a time. Carry on in the one that exists.',
      openExisting: 'Open the existing case',
      conflictSection: 'The conflict',
      conflictFromMatch:
        'Taken from the match. What identifies the conflict cannot be changed here.',
      analysisSection: 'What the matcher found',
      judgeScore: "The AI judge's probability of confusion",
      ruleScore: 'Similarity from a matching rule, not a probability',
      noJustification: 'A rule surfaced this pair; no model has written a reason for it.',
      pickSection: "Your client's trademark",
      client: 'Client',
      clientSearch: 'Search clients…',
      chooseClient: 'Choose a client',
      brand: 'Trademark',
      brandSearch: "Search this client's trademarks…",
      chooseBrand: 'Choose a trademark',
      noBrands: 'This client has no trademarks yet.',
      narrow: 'Showing {shown} of {total}. Type to narrow the list.',
      opposedSection: 'The opposed trademark',
      markName: 'Trademark name',
      markNumber: 'Application number',
      markOwner: 'Applicant or owner',
      registry: 'Registry',
      registryNone: 'Not set',
      handNote:
        'No match stands behind a case opened by hand, so it has no risk score and no deadline from a registry. Add the deadline on the case page.',
      caseSection: 'The case',
      caseTitle: 'Case title',
      caseTitleHint: 'Left empty, the case is called “{title}”.',
      caseNumber: 'Case number',
      caseNumberHint: "The office's reference, once there is one.",
      priority: 'Priority',
      startAs: 'Starts as',
      lawyer: 'Assigned lawyer',
      grounds: 'Grounds',
      groundsPlaceholder: 'e.g. Art. 8(1)(b) EUTMR',
      notes: 'Internal notes',
      notesHint: "Your firm's working notes. Never shown to the client.",
      markDeadline:
        "The found mark's opposition deadline, {date}, becomes the case's first deadline.",
      markDeadlinePassed:
        'The opposition window on this mark closed on {date}. The case records that rather than carrying an overdue deadline.',
      afterNote: 'Documents, deadlines and notes are added on the case page once it exists.',
      pickBrand: "Choose your client's trademark.",
      nameMark: 'Name the opposed trademark.',
      failed: 'The case was not opened',
      created: 'Case opened',
      lawyersFailed: 'The list of lawyers could not be loaded. The case can be assigned later.',
    },
    detail: {
      assigned: '{name} assigned',
      filed: 'Filed {date}',
      backToCases: 'Back to cases',
      addNote: 'Add note',
      upload: 'Upload document',
      uploading: 'Uploading…',
      updateStatus: 'Update status',
      delete: 'Delete case',
      deleteTitle: 'Delete this case?',
      deleteBody:
        'The case, its timeline, notes and files are removed for good. To stop working on it but keep its history, close it instead.',
      deleted: 'Case deleted',
      deleteFailed: 'The case was not deleted',
      readOnly: 'You can read this case but not change it.',
      sections: 'Case sections',
      tabConflict: 'Conflict',
      tabAnalysis: 'Analysis',
      tabNotes: 'Notes',
      tabDocs: 'Documents',
      tabTimeline: 'Timeline',
      protected: "Your client's trademark",
      opposed: 'Opposed trademark',
      yourClient: 'Your client',
      opposedTag: 'Opposed',
      applicationNo: 'Application number',
      registered: 'Registered',
      filedOn: 'Filed',
      classes: 'Nice classes',
      type: 'Type',
      owner: 'Owner',
      windowCloses: 'Opposition deadline',
      handTyped: 'Typed in by hand: only what the lawyer entered is known.',
      analysisJudge: 'Read by the AI judge. Its verdict and its own words are below.',
      analysisRules:
        'Surfaced by a matching rule. No model read this pair, and the number is a similarity, not a probability.',
      score: '{n} / 100',
      verdict: 'Verdict',
      overlap: 'Shared Nice classes',
      noOverlap: 'No Nice class in common',
      onlyTheirs: 'theirs only',
      loadFailed: 'Part of this case could not be loaded.',
      editDetails: 'Edit details',
      saveDetails: 'Save details',
      detailsSaved: 'Case details saved',
      detailsFailed: 'The case details were not saved',
      summaryPdf: 'Case summary (PDF)',
    },
    notes: {
      title: 'Case notes',
      sub: 'Legal findings, internal working notes and status updates.',
      all: 'All',
      placeholder: 'Write a legal note, a finding or an instruction to the team…',
      visible: 'Visible to client',
      visibleHint: "The client's viewer account will see this note.",
      post: 'Post note',
      posting: 'Posting…',
      empty: 'No notes yet.',
      emptyCategory: 'No notes in this category.',
      added: 'Note added',
      failed: 'The note was not saved',
    },
    docs: {
      title: 'Documents',
      sub: 'Filings, evidence, correspondence and decisions.',
      category: 'Category',
      choose: 'Upload a file',
      hint: 'PDF, Word, plain text or an image.',
      visibleShort: 'Client can see',
      hiddenShort: 'Firm only',
      showToClient: 'Show to client',
      hideFromClient: 'Hide from client',
      download: 'Download {name}',
      delete: 'Delete {name}',
      deleteTitle: 'Delete {name}?',
      deleteBody: 'The file is removed from this case. This cannot be undone.',
      empty: 'No documents on this case.',
      uploaded: 'Uploaded {name}',
      failed: 'The upload failed',
      actionFailed: 'That change to the document was not saved',
    },
    timeline: {
      title: 'Case timeline',
      sub: 'Every change to this case, newest first. The history cannot be edited.',
      empty: 'Nothing recorded yet.',
    },
    lifecycle: {
      title: 'Case status',
      moveTo: 'Move to',
      choose: 'Choose the next step',
      outcome: 'How did it end?',
      chooseOutcome: 'Choose an outcome',
      reason: 'Why is the case being closed?',
      reopenReason: 'Why is it being reopened? (optional)',
      filingDate: 'Filing date',
      clientVisible: 'Show this step to the client',
      apply: 'Change status',
      applying: 'Saving…',
      needOutcome: 'Say how the case ended.',
      needReason: 'Say why the case is being closed.',
      changed: 'Status changed to {status}',
      failed: 'The status was not changed',
      noMoves: 'No further steps from here.',
      outcomeLine: 'Outcome: {outcome}',
    },
    deadlines: {
      title: 'Deadlines',
      empty: 'No deadlines on this case.',
      markDone: 'Mark done',
      reopen: 'Reopen',
      delete: 'Delete deadline',
      deleteTitle: 'Delete “{title}”?',
      deleteBody: 'The deadline is removed. The timeline keeps a record that it existed.',
      add: 'Add deadline',
      adding: 'Adding…',
      newTitle: 'What is due',
      newDate: 'Due date',
      newKind: 'Kind',
      newNote: 'Note (optional)',
      failed: 'The deadline was not saved',
    },
    related: {
      title: 'Opposition cases',
      count: '{n} case | {n} cases',
      none: 'No opposition cases yet.',
      viewAll: 'View all',
      create: 'Create opposition',
      failed: 'The cases could not be loaded.',
    },
    match: {
      openCase: 'Open opposition case',
      openCaseHint:
        'Opens a case prefilled from this match. A match can have one open case at a time.',
    },
  },

  /* The clients list (W3.M6, translated 2026-09-30). */
  clients: {
    title: 'Clients & brand owners',
    subtitle:
      'The companies whose trademarks the firm protects: their marks, monitoring and cases.',
    filters: 'Filters',
    add: 'Add client',
    unit: 'clients',
    shown: '{n} of {total} clients',
    filtersActive: '{n} filter active | {n} filters active',
    searchPlaceholder: 'Search clients…',
    unassigned: 'Unassigned',
    kpi: {
      label: 'Clients overview',
      total: 'Clients',
      newThisQuarter: 'new this quarter',
      trademarks: 'Trademarks',
      perClient: '{n} per client on average',
      highRisk: 'High-risk clients',
      highRiskHint: 'Two or more open cases',
      registries: 'Registries',
      registriesHint: 'Where their trademarks are watched',
    },
    filter: {
      name: 'Client name',
      namePlaceholder: 'Company or contact',
      registry: 'Registry',
      monitoring: 'Monitoring',
      risk: 'Risk level',
    },
    chip: {
      risk: 'Risk: {level}',
      search: 'Search: {q}',
    },
    risk: {
      high: 'High',
      med: 'Medium',
      low: 'Low',
      none: 'Not assessed',
    },
    monitoring: {
      full: 'Fully monitored',
      partial: 'Partly monitored',
      paused: 'Monitoring paused',
      none: 'Not monitored',
      unknown: 'Monitoring unknown',
    },
    sort: {
      label: 'Sort clients',
      name: 'Name, A to Z',
      nameDesc: 'Name, Z to A',
      trademarks: 'Most trademarks',
      cases: 'Most open cases',
      recent: 'Recently updated',
    },
    empty: {
      title: 'No clients match your search',
      message: 'Try a different search term or clear the active filters.',
      reset: 'Reset filters',
    },
    col: {
      client: 'Client',
      registries: 'Registries',
      risk: 'Risk',
      lawyer: 'Lawyer',
      trademarks: 'Trademarks',
    },
    preview: {
      label: 'Client overview',
      close: 'Close preview',
      summary: 'Summary',
      registries: '{n} registry | {n} registries',
      monitoring: 'Watched',
      openCases: 'Open cases',
      allMarks: 'All {n}',
      noMarks: 'No trademarks on file.',
      registered: 'Registered',
      pending: 'Pending',
      classes: 'Class {list} | Classes {list}',
      lawyers: 'Lawyers',
      activity: 'Recent case activity',
      noActivity: 'No case activity yet.',
      open: 'Open client dossier',
    },
    activity: {
      title: 'Recent case activity',
      sub: 'The newest lines from the timelines of every case, across all clients.',
      empty: 'No case activity yet.',
    },
    quick: {
      title: 'Quick actions',
      addClient: 'Add a company whose trademarks the firm protects.',
      addTrademark: 'Add trademark',
      addTrademarkSub: "Put a mark on a client's portfolio and choose where it is watched.",
      monitoring: 'Open monitoring',
      monitoringSub: "Review what the monitoring found against your clients' marks.",
      case: 'Open a case',
      caseSub: 'Start an opposition case for a client.',
    },
  },

  /* One client's dossier (W3.M6, rebuilt on real data 2026-09-30). */
  client: {
    back: 'Back to clients',
    edit: 'Edit client',
    eyebrow: 'Client · {id}',
    riskClient: 'Risk: {level}',
    trademarkCount: '{n} trademark | {n} trademarks',
    openCaseCount: '{n} open case | {n} open cases',
    contact: 'Contact: {name}',
    monitoring: 'Monitoring',
    watchedOf: '{n} of {total} trademarks watched',
    openMonitoring: 'Open monitoring',
    newCase: 'New case',
    sections: 'Client sections',
    failed: 'This could not be loaded. Reload the page to try again.',
    notWatched: 'Not watched',
    classes: 'Class {list} | Classes {list}',
    registeredOn: 'Registered {date}',
    kpi: {
      label: 'Client overview',
      coverage: 'Monitoring coverage',
      casesOpen: 'Open cases need a response',
      casesNone: 'No open cases',
    },
    tab: {
      portfolio: 'Portfolio',
      cases: 'Cases',
      people: 'People',
      matches: 'Matches',
      activity: 'Activity',
      monitoring: 'Monitoring',
      documents: 'Documents',
    },
    portfolio: {
      title: 'Trademark portfolio',
      sub: 'Every mark on file for this client, registered or pending.',
      all: 'View all in My Trademarks',
      emptyTitle: 'No trademarks yet',
      empty: 'This client has no marks on file.',
    },
    people: {
      title: 'People with access',
      sub: "Lawyers working on this client and the client's own users. The firm's main accounts see every client and are not listed.",
      empty: 'Nobody has been given access to this client yet.',
      add: 'Give someone access',
      manager: 'Lawyer',
      viewer: 'Client user',
      off: 'Switched off',
      wholeCompany: 'Whole company',
      someMarks: '{n} trademark | {n} trademarks',
    },
    matches: {
      title: 'Open matches',
      sub: "What the monitoring found against this client's trademarks, closing windows first.",
      lastScan: 'Last scan {date}',
      awaiting: 'awaiting review',
      high: 'high risk',
      closingSoon: 'high risk closing within 14 days',
      stillOpen: 'with the window still open',
      none: 'No match with an opposition window still open.',
      against: 'against {brand}',
    },
    activity: {
      title: 'Case activity',
      sub: "The newest lines from this client's opposition cases.",
      empty: 'No case activity for this client yet.',
    },
    coverage: {
      title: 'Monitoring by registry',
      sub: "How many of this client's trademarks are watched on each registry.",
      ofMarks: 'of {n} trademark | of {n} trademarks',
      lastScan: 'Last scan {date}',
      waiting: 'Waiting for its first scan',
      notWatched: 'None watched here',
    },
    docs: {
      title: 'Documents',
      sub: "Files on this client's trademarks. Add them on the trademark's own page.",
      empty: "No documents on this client's trademarks.",
      open: 'Open {name}',
      openFailed: 'Could not open the file',
      kind: {
        logo: 'Logo',
        certificate: 'Certificate',
        evidence: 'Evidence',
        correspondence: 'Correspondence',
        other: 'Other',
      },
    },
  },

  /* The pages translated in the Wave 3 pass (2026-09-30), one file per area
   * so they could be written side by side. */
  ...monitoring,
  ...trademarks,
  ...general,
  ...people,
  ...explain,
} as const
